import { ABDURASHIDSUFI_EDITION_ID, quranApi, getMp3quranAbdurashidsufiSurahUrl } from '@/lib/quranApi'
import { surahs as surahCatalog } from '@/data/surahs'

/** Verse/surah MP3 URLs (see quranApi for per-reciter CDN bitrates). */
export const CDN_AUDIO_BITRATE = 128

export function formatSurahListItem(s: unknown): string {
  if (typeof s === 'string') return s
  if (!s || typeof s !== 'object') return ''
  const o = s as Record<string, unknown>
  const num = typeof o.number === 'number' ? o.number : undefined
  const pick = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : '')
  let label =
    pick(o.name) ||
    pick(o.englishName) ||
    pick(o.english_name) ||
    pick(o.translation) ||
    ''

  if (num != null && !label) {
    const meta = surahCatalog.find((x) => x.number === num)
    label = meta?.englishName || meta?.name || ''
  }

  if (num != null && label) {
    return `Surah ${num} · ${label}`
  }
  if (num != null) {
    return `Surah ${num}`
  }
  return label
}

export function getAyahImageUrl(
  surahNumber: number,
  ayahNumber: number,
  selectedFont: string,
  highResolution: boolean,
) {
  return quranApi.getCDNAyahImage(surahNumber, ayahNumber, selectedFont, highResolution)
}

export function getSurahAudioUrl(surahNumber: number, selectedReciter: string, audioBitrate: number) {
  return quranApi.getCDNAudioBySurah(surahNumber, selectedReciter, audioBitrate)
}

export function getGlobalAyahId(ayah: any): number {
  const fromQf = Number(ayah?.ayahId ?? ayah?.id)
  if (Number.isFinite(fromQf) && fromQf > 0) return fromQf
  const global = Number(ayah?.number)
  if (Number.isFinite(global) && global > 0 && global <= 6236) return global
  return 0
}

export function getAyahAudioUrl(ayah: any, selectedReciter: string, audioBitrate: number): string {
  if (selectedReciter === ABDURASHIDSUFI_EDITION_ID) {
    const sn = getSurahNumber(ayah)
    if (!sn) return ''
    return getMp3quranAbdurashidsufiSurahUrl(sn)
  }
  const ayahId = getGlobalAyahId(ayah)
  if (!ayahId) return ''
  return quranApi.getCDNAudioByAyah(ayahId, selectedReciter, audioBitrate)
}

/** Global ayah id (standard 6236 numbering) of the first ayah of a surah. */
function getSurahGlobalStart(surahNumber: number): number {
  let start = 1
  for (const s of surahCatalog) {
    if (s.number >= surahNumber) break
    start += s.numberOfAyahs
  }
  return start
}

/**
 * Full-surah playlist (every ayah), independent of what's rendered on screen — so
 * "Play surah" plays the complete surah even in single-page reading mode. Uses the
 * standard global ayah numbering for islamic.network; Abdul Rashid Ali Sufi returns
 * his single gapless surah MP3.
 */
export function getFullSurahPlaylist(
  surahNumber: number,
  selectedReciter: string,
  audioBitrate: number,
): string[] {
  if (!surahNumber) return []
  if (selectedReciter === ABDURASHIDSUFI_EDITION_ID) {
    const url = getMp3quranAbdurashidsufiSurahUrl(surahNumber)
    return url ? [url] : []
  }
  const meta = surahCatalog.find((s) => s.number === surahNumber)
  if (!meta) return []
  const start = getSurahGlobalStart(surahNumber)
  const urls: string[] = []
  for (let i = 0; i < meta.numberOfAyahs; i++) {
    const url = quranApi.getCDNAudioByAyah(start + i, selectedReciter, audioBitrate)
    if (url) urls.push(url)
  }
  return urls
}

const ISLAMIC_AYAH_AUDIO_RE = /(https:\/\/cdn\.islamic\.network\/quran\/audio\/)(\d+)(\/[^/]+\/\d+\.mp3)/

/**
 * If an islamic.network per-ayah URL fails (e.g. a reciter only exists at a non-default
 * bitrate), return the next untried bitrate variant so playback can recover. Returns null
 * when the URL isn't a CDN ayah URL or all bitrates were tried.
 */
export function alternateAyahAudioUrl(url: string, tried: Set<string>): string | null {
  const m = ISLAMIC_AYAH_AUDIO_RE.exec(url)
  if (!m) return null
  for (const br of [128, 64, 192, 48]) {
    const candidate = `${m[1]}${br}${m[3]}`
    if (!tried.has(candidate)) return candidate
  }
  return null
}

export function getSurahNumber(ayah: any): number {
  return ayah?.surah?.number || ayah?.surahNumber || ayah?.surah || 1
}

export function getAyahNumber(ayah: any): number {
  return ayah?.numberInSurah || ayah?.number || ayah?.verseNumber || 1
}

/** Primary Arabic string from verse payloads (APIs vary between `text` and `arabic_text`). */
export function getAyahArabicText(ayah: any): string {
  return String(ayah?.text ?? ayah?.arabic_text ?? '').trim()
}

export function isBismillah(ayah: any): boolean {
  const surahNumber = getSurahNumber(ayah)
  const ayahNumber = getAyahNumber(ayah)
  if (surahNumber === 9) return false
  if (ayahNumber !== 1) return false
  const text = getAyahArabicText(ayah)
  const bismillahPattern = /بِسْمِ[\s\S]{0,120}?(?:الرَّحْمَ|ٱلرَّحْمَ)[\s\S]{0,80}?(?:الرَّحِيم|ٱلرَّحِيم)/
  return (
    bismillahPattern.test(text) ||
    text.includes('بِسْمِ') ||
    text.includes('بسم الله') ||
    /بِسْمِ\s*[\u0671]?للَّهِ/u.test(text)
  )
}

export function getFontClass(font: string) {
  const fontClasses: Record<string, string> = {
    indopak: 'font-indopak',
    tajweed: 'font-tajweed',
  }
  return fontClasses[font] || 'font-tajweed'
}

export function getFontStyle(font: string) {
  const fontStyles: Record<string, string> = {
    indopak: "font-family: 'IndoPak', 'IndoPakPreferredLocal', 'QuranIndoPak', 'QuranUthmaniHafs', 'Noto Nastaliq Urdu', 'Noto Naskh Arabic', 'Scheherazade New', 'Amiri Quran', serif;",
    tajweed: "font-family: 'QuranUthmaniHafs', 'Scheherazade New', 'Amiri Quran', 'Amiri', serif;",
  }
  return fontStyles[font] || fontStyles.tajweed
}

export function getAyahKey(ayah: any): string {
  return `${getSurahNumber(ayah)}_${getAyahNumber(ayah)}`
}

export function getVerseKeyLabel(ayah: any): string {
  const s = getSurahNumber(ayah)
  const v = getAyahNumber(ayah)
  if (!s || !v) return ''
  return `${s}:${v}`
}

const tajweedColorMap: Record<string, string> = {
  ham_wasl: '#60a5fa',
  laam_shamsiyah: '#4ade80',
  slnt: '#94a3b8',
  ghn: '#22c55e',
  idgham: '#38bdf8',
  idmgh_ghn: '#38bdf8',
  idmgh_w_ghn: '#0284c7',
  iqlb: '#f59e0b',
  ikhf: '#eab308',
  qlq: '#f97316',
  qalaqah: '#f97316',
  madda_normal: '#f472b6',
  madda_permissible: '#ec4899',
  madda_necessary: '#db2777',
  madda_obligatory: '#db2777',
  ghunna: '#22c55e',
}

export function getTajweedHtml(ayah: any): string {
  const raw = String(ayah?.textTajweed || getAyahArabicText(ayah) || '')
  if (!raw) return ''
  return raw
    .replace(/<span[^>]*\bclass\s*=\s*["']?end["']?[^>]*>[\s\S]*?<\/span>/gi, '')
    .replace(/<tajweed\s+class=([a-zA-Z0-9_-]+)>/g, (_full, tajweedClass: string) => {
      const color = tajweedColorMap[tajweedClass] || '#22d3ee'
      return `<span class="tajweed-mark tajweed-${tajweedClass}" style="color:${color}">`
    })
    .replace(/<\/tajweed>/g, '</span>')
}

export function ayahHasColoredTajweed(ayah: any) {
  const tw = String(ayah?.textTajweed ?? '')
  const tx = String(ayah?.text ?? '')
  return /<tajweed\s/i.test(tw) || /<tajweed\s/i.test(tx)
}

export function getArabicHtmlForAyah(ayah: any, wantsTajweedMarkup: boolean): string {
  if (wantsTajweedMarkup && ayahHasColoredTajweed(ayah)) return getTajweedHtml(ayah)
  return getAyahArabicText(ayah)
}

export function getArabicFontKeyForAyah(ayah: any, wantsTajweedMarkup: boolean, selectedFont: string): string {
  if (wantsTajweedMarkup && ayahHasColoredTajweed(ayah)) return 'tajweed'
  return selectedFont
}

export function getTranslationForAyah(ayah: any, translationPageData: any): string {
  const tData = translationPageData
  if (!tData?.ayahs) return ''
  const s = Number(getSurahNumber(ayah))
  const v = Number(getAyahNumber(ayah))
  const match = tData.ayahs.find((a: any) => {
    const ts = Number(a?.surah?.number || a?.surahNumber || a?.surah || 0)
    const tv = Number(a?.numberInSurah || a?.number || a?.verseNumber || 0)
    return ts === s && tv === v
  })
  return match?.text || ''
}

/**
 * Split after the Medina-style basmalah when regex stripping misses (mixed ٱ/ا, spacing, diacritics).
 * Does not apply to Al-Fatiha alone (rest empty → caller treats as no split).
 *
 * APIs (e.g. alquran.cloud) often use ر + شدة + فتحة on رَّحِيم while typed literals use ر + فتحة + شدة,
 * so we match الرَّحِيم with flexible combining marks instead of a fixed glyph sequence.
 */
function splitAfterMedinaBasmalah(t: string): { bismillah: string; rest: string } | null {
  const s = t.trim().normalize('NFC')
  if (!s.startsWith('بِسْمِ')) return null
  const scan = s.slice(0, Math.min(s.length, 420))
  const re =
    /(?:ٱ|ا)\u0644\u0631(?:[\u064b-\u065f])*[\u062d]\u0650\u064a\u0645(?:[\u064e-\u065f\u064b-\u064d\u064c]*)?/gu
  let lastEnd = -1
  let m: RegExpExecArray | null
  while ((m = re.exec(scan)) !== null) {
    lastEnd = m.index + m[0].length
  }
  if (lastEnd < 0) return null
  let j = lastEnd
  while (j < s.length && /\s/u.test(s[j]!)) j += 1
  const rest = s.slice(j).trim()
  if (!rest) return null
  return { bismillah: s.slice(0, j).trim(), rest }
}

export function stripBismillahFromText(text: string): string {
  if (!text) return ''
  const patterns: RegExp[] = [
    /^\uFDFD[\s،,:;\-–—]*\s*/u,
    // Plain ا (no wasla) — common in alquran.cloud JSON
    /^\s*بِسْمِ\s+اللَّهِ\s+الرَّحْمَ[َٰ]?نِ\s+الرَّحِيمِ?\s*[،,:;\-–—۞]*\s*/u,
    // Uthmani with alef wasla ٱ (U+0671) — common on Quran.com / alquran.cloud
    /^\s*بِسْمِ\s*ٱللَّهِ\s*ٱلرَّحْمَـٰنِ\s*ٱلرَّحِيمِ\s*[،,:;\-–—۞]*\s*/u,
    /^\s*بِسْمِ\s*ٱللَّهِ\s*ٱلرَّحْمَ[َٰ]?نِ\s*ٱلرَّحِيمِ?\s*[،,:;\-–—۞]*\s*/u,
    /^\s*بِسْمِ\s*ٱللَّهِ\s*ٱلرَّحْمَٰنِ\s*ٱلرَّحِيم\s*[،,:;\-–—۞]*\s*/u,
    /^\s*بِسْمِ\s*اللَّهِ\s*الرَّحْمَ[َٰ]?نِ\s*الرَّحِيمِ?\s*[،,:;\-–—۞]*\s*/u,
    /^\s*بِسْمِ\s*اللَّهِ\s*الرَّحْمَٰنِ\s*الرَّحِيم\s*[،,:;\-–—]*\s*/u,
    /^\s*بِسْمِ\s*اللّٰهِ\s*الرَّحْمٰنِ\s*الرَّحِيم\s*[،,:;\-–—]*\s*/u,
    /^\s*بِسْمِ\s*اللّهِ\s*الرَّحْمٰنِ\s*الرَّحِيم\s*[،,:;\-–—]*\s*/u,
    /^\s*بِسْمِ\s*اللّه\s*الرَّحْمٰن\s*الرَّحِيم\s*[،,:;\-–—]*\s*/u,
  ]
  let result = text
  for (const rx of patterns) {
    result = result.replace(rx, '')
  }
  result = result.trim()
  if (result === text.trim()) {
    const loose = splitAfterMedinaBasmalah(text)
    if (loose) return loose.rest
  }
  return result.trim()
}

/** Splits leading basmalah from the rest of ayah 1 text (surahs after At-Tawbah). */
export function extractBismillahAndRest(text: string): { bismillah: string; rest: string } {
  const raw = String(text || '').trim()
  if (!raw) return { bismillah: '', rest: '' }

  const strictRest = stripBismillahFromText(raw)
  if (strictRest && strictRest !== raw) {
    let idx = raw.indexOf(strictRest)
    if (idx < 0) {
      const trimmed = strictRest.replace(/^\s+/, '')
      idx = raw.indexOf(trimmed)
    }
    if (idx > 0) return { bismillah: raw.slice(0, idx).trim(), rest: strictRest.trim() }
  }

  const loose = splitAfterMedinaBasmalah(raw)
  if (loose) return { bismillah: loose.bismillah.trim(), rest: loose.rest.trim() }

  return { bismillah: '', rest: raw }
}

export function getVerseTextWithoutBismillah(ayah: any): string {
  const text: string = getAyahArabicText(ayah)
  const surah = getSurahNumber(ayah)
  const ayahNum = getAyahNumber(ayah)
  if (ayahNum === 1 && surah !== 9) {
    if (surah === 1) return ''
    return stripBismillahFromText(text)
  }
  return text
}

/** Ayah shaped for rendering only the verse body (not the opening basmalah). */
export function ayahWithVerseTextOnly(ayah: any): any {
  const surah = getSurahNumber(ayah)
  const ayahNum = getAyahNumber(ayah)
  if (ayahNum !== 1 || surah === 9 || surah === 1) return ayah
  const verse = getVerseTextWithoutBismillah(ayah)
  const tw = String(ayah?.textTajweed ?? '').trim()
  const verseTajweed = /<tajweed\s/i.test(tw) ? tw : ''
  return {
    ...ayah,
    text: verse,
    arabic_text: verse,
    textTajweed: verseTajweed,
  }
}

export function ayahWithBismillahLineOnly(ayah: any): any {
  const { bismillah } = extractBismillahAndRest(getAyahArabicText(ayah))
  return { ...ayah, text: bismillah, arabic_text: bismillah, textTajweed: '' }
}

export function getTransliterationWithoutBismillah(ayah: any): string {
  const translit: string = ayah?.transliteration || ''
  if (!translit) return ''
  const surah = getSurahNumber(ayah)
  const ayahNum = getAyahNumber(ayah)
  if (ayahNum === 1 && surah !== 9) {
    const patterns: RegExp[] = [
      /^\s*bismillah(?:i)?(?:\s+ar-rahman)?(?:\s+ar-rahim)?[\s,;:–—-]*\s*/i,
      /^\s*bismillahir\s*rahmanir\s*rahim[\s,;:–—-]*\s*/i,
    ]
    let result = translit
    for (const rx of patterns) {
      result = result.replace(rx, '')
    }
    return result.trim()
  }
  return translit
}
